/**
 * Data Status Page (Deauthorize Callback)
 *
 * Public page required by Meta App Review.
 * Backend returns this URL in the deauthorize callback response:
 *   ${CLIENT_URL}/data-status?code=${confirmationCode}
 *
 * Displays confirmation that the user's Facebook/Instagram account
 * has been disconnected from BuzzBit.
 */

import { useSearchParams } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';

const TOC_SECTIONS = [
  { id: 'status', title: 'Disconnection Status' },
  { id: 'what-next', title: 'What Happens Next' },
  { id: 'reconnect', title: 'Reconnecting' },
  { id: 'contact', title: 'Contact Us' },
];

export default function DataStatus() {
  const [searchParams] = useSearchParams();
  const confirmationCode = searchParams.get('code') || 'N/A';

  return (
    <LegalPageLayout
      title="Account Disconnected"
      subtitle="Your Facebook/Instagram account has been disconnected from BuzzBit."
      effectiveDate="February 25, 2026"
      lastUpdated="February 25, 2026"
      sections={TOC_SECTIONS}
    >
      {/* Disconnection Status */}
      <section id="status" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Disconnection Status</h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-blue-800 font-semibold text-lg">Account Disconnected</p>
              <p className="text-blue-700 mt-1">
                Your Facebook/Instagram account has been successfully disconnected from BuzzBit.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <p className="text-gray-600 mb-2">
            <strong className="text-gray-900">Confirmation Code:</strong>
          </p>
          <p className="font-mono text-lg text-gray-900 bg-white border border-gray-200 rounded px-4 py-2 inline-block">
            {confirmationCode}
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Please save this confirmation code for your records.
          </p>
        </div>
      </section>

      {/* What Happens Next */}
      <section id="what-next" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">What Happens Next</h2>
        <ul className="list-disc ml-6 space-y-3 text-gray-600">
          <li>
            <strong className="text-gray-900">Access Revoked:</strong> BuzzBit will no longer be able to access your Facebook or Instagram account data.
          </li>
          <li>
            <strong className="text-gray-900">Existing Data:</strong> Any data previously collected while connected will be retained in accordance with our Privacy Policy and applicable data protection laws.
          </li>
          <li>
            <strong className="text-gray-900">Active Campaigns:</strong> Any active campaigns or automations using your Facebook/Instagram integration will be paused.
          </li>
          <li>
            <strong className="text-gray-900">Data Deletion:</strong> If you also wish to delete your data, please visit your BuzzBit Privacy Center or contact our Data Protection Officer.
          </li>
        </ul>
      </section>

      {/* Reconnecting */}
      <section id="reconnect" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Reconnecting</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          If you disconnected by mistake or wish to reconnect your Facebook/Instagram account:
        </p>
        <ol className="list-decimal ml-6 space-y-2 text-gray-600">
          <li>Log in to your BuzzBit workspace</li>
          <li>Navigate to Settings &rarr; Integrations &rarr; Meta</li>
          <li>Click "Connect Facebook/Instagram" and follow the authorization flow</li>
        </ol>
      </section>

      {/* Contact Us */}
      <section id="contact" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Contact Us</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          If you have questions about your account disconnection, please contact us:
        </p>
        <div className="bg-gray-50/50 border border-gray-200 rounded-lg p-6 space-y-3">
          <p className="text-gray-600">
            <strong className="text-gray-900">General Support:</strong>{' '}
            <a href="mailto:support@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">
              support@buzzbitx.com
            </a>
          </p>
          <p className="text-gray-600">
            <strong className="text-gray-900">Privacy Inquiries:</strong>{' '}
            <a href="mailto:support@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">
              support@buzzbitx.com
            </a>
          </p>
        </div>
      </section>
    </LegalPageLayout>
  );
}
